import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  IdentificationCard, Cards, Plus, Trash, Upload, Sparkle, Crop, Eye, FileText, Download,
} from '@phosphor-icons/react';
import { customerApi, formatApiError } from '../services/api';
import CustomerAvatar from './CustomerAvatar';

/* ---------- Helpers ---------- */

// Crop a file to the given pixel area (returns Blob)
const getCroppedBlob = async (imageSrc, pixelCrop, maxOutDim = 600) => {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, maxOutDim / Math.max(pixelCrop.width, pixelCrop.height));
  canvas.width = Math.round(pixelCrop.width * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, canvas.width, canvas.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
};

const fetchAuthedAsBlob = async (url) => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Bild konnte nicht geladen werden (${res.status})`);
  return res.blob();
};

/* ---------- Crop Modal ---------- */

const CropModal = ({ open, customerId, sourceDocType, onClose, onSaved }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imgDim, setImgDim] = useState({ w: 0, h: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      return;
    }
    (async () => {
      try {
        const blob = await fetchAuthedAsBlob(customerApi.documentUrl(customerId, sourceDocType));
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageSrc(reader.result);
          const tmp = new Image();
          tmp.onload = () => setImgDim({ w: tmp.width, h: tmp.height });
          tmp.src = reader.result;
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        toast.error(formatApiError(e) || e.message);
        onClose();
      }
    })();
  }, [open, customerId, sourceDocType, onClose]);

  const onCropComplete = useCallback((_, areaPx) => setCroppedAreaPixels(areaPx), []);

  const useAiSuggestion = async () => {
    if (!imgDim.w || !imgDim.h) return;
    setAiBusy(true);
    try {
      const res = await customerApi.extractFace(customerId, sourceDocType);
      const { x_pct, y_pct, w_pct, h_pct } = res.data || {};
      // react-easy-crop uses crop+zoom relative to image; easiest is to set zoom + center
      // We'll compute a target pixel rect, then reverse-engineer.
      const cropW = (w_pct / 100) * imgDim.w;
      const cropH = (h_pct / 100) * imgDim.h;
      const cropX = (x_pct / 100) * imgDim.w + cropW / 2;
      const cropY = (y_pct / 100) * imgDim.h + cropH / 2;
      // Aspect = 1, so target square in image space:
      const side = Math.min(cropW, cropH);
      const newZoom = Math.max(1, Math.min(imgDim.w, imgDim.h) / side);
      setZoom(newZoom);
      // crop position is offset from image center (in image px)
      setCrop({
        x: -(cropX - imgDim.w / 2),
        y: -(cropY - imgDim.h / 2),
      });
      toast.success('KI-Vorschlag übernommen — Feinjustierung möglich');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setLoading(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      await customerApi.uploadDocument(customerId, 'profile', file);
      toast.success('Profilbild aktualisiert');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Profilbild aus Ausweis erstellen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative w-full bg-black rounded-sm overflow-hidden" style={{ height: 360 }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />
            )}
          </div>

          <div>
            <Label className="overline">Zoom</Label>
            <input
              type="range"
              min={1} max={5} step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
              data-testid="crop-zoom"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={useAiSuggestion} disabled={aiBusy} data-testid="ai-suggest-btn">
              <Sparkle weight="fill" size={14} className={`mr-1 ${aiBusy ? 'animate-pulse' : ''}`} />
              {aiBusy ? 'KI sucht Gesicht…' : 'KI-Vorschlag (Gemini Vision)'}
            </Button>
            <p className="text-xs text-[#8A94A6] flex-1 self-center">
              Ziehe das runde Auswahlfenster auf das Passfoto. KI-Vorschlag positioniert es automatisch.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={loading || !croppedAreaPixels} className="btn-primary" data-testid="crop-save-btn">
            {loading ? 'Speichern…' : 'Als Profilbild speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Document Slot ---------- */

const DocSlot = ({ customerId, docType, label, icon: Icon, meta, onChanged, onCropFromHere, isAdmin }) => {
  const fileRef = React.useRef();
  const [busy, setBusy] = useState(false);
  const isPdf = (meta?.content_type || '').includes('pdf');
  const previewUrl = meta ? customerApi.documentUrl(customerId, docType) : null;

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      await customerApi.uploadDocument(customerId, docType, f);
      toast.success(`${label} hochgeladen`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`${label} löschen?`)) return;
    try {
      await customerApi.deleteDocument(customerId, docType);
      toast.success(`${label} gelöscht`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="border border-[#E2E4E9] bg-white rounded-sm p-3" data-testid={`doc-slot-${docType}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-[#525866]" />
          <span className="text-sm font-semibold">{label}</span>
        </div>
        {meta && (
          <div className="flex gap-1">
            {!isPdf && docType.startsWith('id_') || docType.startsWith('dl_') ? (
              <Button variant="ghost" size="sm" type="button" onClick={() => onCropFromHere(docType)} title="Profilbild daraus erstellen">
                <Crop size={14} />
              </Button>
            ) : null}
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" title="Anzeigen">
              <Button variant="ghost" size="sm" type="button"><Eye size={14} /></Button>
            </a>
            {isAdmin && (
              <Button variant="ghost" size="sm" type="button" onClick={handleDelete} title="Löschen">
                <Trash size={14} className="text-red-600" />
              </Button>
            )}
          </div>
        )}
      </div>

      {meta ? (
        isPdf ? (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 px-3 py-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm hover:bg-white">
            <FileText size={20} className="text-[#E60000]" />
            <span className="flex-1 truncate">{meta.filename}</span>
            <Download size={14} />
          </a>
        ) : (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <img src={previewUrl} alt={label} className="w-full h-32 object-cover rounded-sm" />
          </a>
        )
      ) : (
        isAdmin ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="w-full h-32 border-2 border-dashed border-[#8A94A6] rounded-sm flex flex-col items-center justify-center text-[#525866] hover:bg-[#F7F8FA]"
            data-testid={`upload-${docType}-btn`}
          >
            <Upload size={20} />
            <span className="text-xs mt-1">{busy ? 'Lädt hoch…' : 'Klicken zum Hochladen'}</span>
          </button>
        ) : (
          <p className="text-xs text-[#8A94A6] italic h-32 flex items-center justify-center">Nicht hochgeladen</p>
        )
      )}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
    </div>
  );
};

/* ---------- Main Section ---------- */

const CustomerDocumentsSection = ({ customer, onChanged, isAdmin = true }) => {
  const [cropOpen, setCropOpen] = useState(false);
  const [cropFrom, setCropFrom] = useState('id_front');
  const profileFileRef = React.useRef();
  const additionalFileRef = React.useRef();
  const [additionalName, setAdditionalName] = useState('');

  const docs = customer?.documents || {};

  const onProfileSelect = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      await customerApi.uploadDocument(customer.id, 'profile', f);
      toast.success('Profilbild hochgeladen');
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Profilbild entfernen?')) return;
    try {
      await customerApi.deleteDocument(customer.id, 'profile');
      toast.success('Profilbild entfernt');
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onAdditionalSelect = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      await customerApi.uploadDocument(customer.id, 'additional', f, additionalName || f.name);
      setAdditionalName('');
      toast.success('Dokument hinzugefügt');
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDeleteAdditional = async (docId) => {
    if (!window.confirm('Dokument löschen?')) return;
    try {
      await customerApi.deleteDocument(customer.id, 'additional', docId);
      toast.success('Dokument gelöscht');
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="card-swiss p-6 space-y-6" data-testid="customer-documents-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Kundendatei — Dokumente & Profilbild</h2>
          <p className="text-xs text-[#8A94A6]">
            Ausweis- und Führerschein-Scans sind nur für Admins sichtbar (DSGVO).
          </p>
        </div>
      </div>

      {/* Profile image header */}
      <div className="flex items-center gap-4 p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
        <CustomerAvatar customer={customer} size={88} className="ring-2 ring-white shadow" />
        <div className="flex-1">
          <p className="text-sm font-semibold">{customer.first_name} {customer.last_name}</p>
          <p className="text-xs text-[#8A94A6]">
            {customer.profile_image
              ? 'Profilbild gesetzt — sichtbar in Kundenliste, Buchungsliste und Buchungsdetails.'
              : 'Kein Profilbild. Du kannst eines hochladen oder aus dem Ausweis ausschneiden.'}
          </p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={() => profileFileRef.current?.click()} data-testid="upload-profile-btn">
              <Upload size={14} className="mr-1" /> Profilbild hochladen
            </Button>
            {isAdmin && docs.id_front && (
              <Button type="button" variant="outline" size="sm" onClick={() => { setCropFrom('id_front'); setCropOpen(true); }} data-testid="crop-from-id-btn">
                <Crop size={14} className="mr-1" /> Aus Ausweis ausschneiden
              </Button>
            )}
            {isAdmin && docs.dl_front && (
              <Button type="button" variant="outline" size="sm" onClick={() => { setCropFrom('dl_front'); setCropOpen(true); }} data-testid="crop-from-dl-btn">
                <Crop size={14} className="mr-1" /> Aus Führerschein ausschneiden
              </Button>
            )}
            {customer.profile_image && (
              <Button type="button" variant="ghost" size="sm" onClick={handleDeleteProfile} data-testid="delete-profile-btn">
                <Trash size={14} className="mr-1 text-red-600" /> Entfernen
              </Button>
            )}
          </div>
          <input ref={profileFileRef} type="file" accept="image/*" className="hidden" onChange={onProfileSelect} />
        </div>
      </div>

      {isAdmin && (
        <>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-2 text-[#525866]">Personalausweis</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DocSlot customerId={customer.id} docType="id_front" label="Vorderseite" icon={IdentificationCard}
                       meta={docs.id_front} onChanged={onChanged} isAdmin={isAdmin}
                       onCropFromHere={(t) => { setCropFrom(t); setCropOpen(true); }} />
              <DocSlot customerId={customer.id} docType="id_back" label="Rückseite" icon={IdentificationCard}
                       meta={docs.id_back} onChanged={onChanged} isAdmin={isAdmin}
                       onCropFromHere={(t) => { setCropFrom(t); setCropOpen(true); }} />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-2 text-[#525866]">Führerschein</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DocSlot customerId={customer.id} docType="dl_front" label="Vorderseite" icon={Cards}
                       meta={docs.dl_front} onChanged={onChanged} isAdmin={isAdmin}
                       onCropFromHere={(t) => { setCropFrom(t); setCropOpen(true); }} />
              <DocSlot customerId={customer.id} docType="dl_back" label="Rückseite" icon={Cards}
                       meta={docs.dl_back} onChanged={onChanged} isAdmin={isAdmin}
                       onCropFromHere={(t) => { setCropFrom(t); setCropOpen(true); }} />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-2 text-[#525866]">Weitere Dokumente</h3>
            <div className="space-y-2">
              {(docs.additional || []).map((d) => {
                const isPdf = (d.content_type || '').includes('pdf');
                const url = customerApi.documentUrl(customer.id, 'additional', d.id);
                return (
                  <div key={d.id} className="flex items-center gap-3 p-3 bg-white border border-[#E2E4E9] rounded-sm" data-testid={`additional-${d.id}`}>
                    {isPdf ? <FileText size={20} className="text-[#E60000]" /> : <img src={url} alt={d.name} className="w-10 h-10 object-cover rounded-sm" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.name || d.filename}</p>
                      <p className="text-xs text-[#8A94A6]">{Math.ceil((d.size || 0)/1024)} KB · {new Date(d.uploaded_at).toLocaleDateString('de-DE')}</p>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" type="button"><Eye size={14} /></Button>
                    </a>
                    <Button variant="ghost" size="sm" type="button" onClick={() => handleDeleteAdditional(d.id)}>
                      <Trash size={14} className="text-red-600" />
                    </Button>
                  </div>
                );
              })}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="overline">Bezeichnung (optional)</Label>
                  <Input value={additionalName} onChange={(e) => setAdditionalName(e.target.value)} placeholder="z.B. Versicherungspolice" data-testid="additional-name-input" />
                </div>
                <Button type="button" variant="outline" onClick={() => additionalFileRef.current?.click()} data-testid="upload-additional-btn">
                  <Plus size={14} className="mr-1" /> Hochladen
                </Button>
                <input ref={additionalFileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onAdditionalSelect} />
              </div>
            </div>
          </div>
        </>
      )}

      <CropModal
        open={cropOpen}
        customerId={customer.id}
        sourceDocType={cropFrom}
        onClose={() => setCropOpen(false)}
        onSaved={onChanged}
      />
    </div>
  );
};

export default CustomerDocumentsSection;
